/**
 * Dorvahi premium layer
 * - Wishlist (saved in this browser via localStorage) with header counter + drawer
 * - Quick View modal (product JSON, variant picker, add to bag via Horizon cart events)
 * - Scroll reveal + header scroll state
 */

const STORAGE_KEY = 'dorvahi:wishlist';
const config = window.DorvahiConfig || {};
const root = (config.root || '/').replace(/\/?$/, '/');

/* ------------------------------------------------------------------ */
/* Utilities                                                          */
/* ------------------------------------------------------------------ */

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const sizedImage = (src, width) => {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
};

let formatMoneyFn = null;
import('@theme/money-formatting')
  .then((mod) => (formatMoneyFn = mod.formatMoney))
  .catch(() => {});

const money = (cents) => {
  const currency = config.currency || 'INR';
  if (formatMoneyFn && config.moneyFormat) {
    try {
      return formatMoneyFn(cents, config.moneyFormat, currency);
    } catch (e) {}
  }
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
};

const toastEl = () => document.querySelector('[data-dv-toast]');
let toastTimer;
const toast = (message) => {
  const el = toastEl();
  if (!el) return;
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
};

const openDialog = (dialog) => {
  if (!dialog || dialog.open) return;
  dialog.showModal();
  document.documentElement.classList.add('dv-scroll-lock');
  requestAnimationFrame(() => dialog.classList.add('is-open'));
};

const closeDialog = (dialog) => {
  if (!dialog || !dialog.open) return;
  dialog.classList.remove('is-open');
  const finish = () => {
    dialog.close();
    if (!document.querySelector('dialog.dv-drawer[open], dialog.dv-qv[open], dialog.dv-sc[open]')) {
      document.documentElement.classList.remove('dv-scroll-lock');
    }
  };
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  reduce ? finish() : setTimeout(finish, 320);
};

/* ------------------------------------------------------------------ */
/* Wishlist                                                           */
/* ------------------------------------------------------------------ */

const Wishlist = {
  read() {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  },
  write(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {}
    this.render();
  },
  has(handle) {
    return this.read().some((item) => item.handle === handle);
  },
  toggle(item) {
    const items = this.read();
    const index = items.findIndex((i) => i.handle === item.handle);
    if (index > -1) {
      items.splice(index, 1);
      this.write(items);
      toast('Removed from wishlist');
      return false;
    }
    items.unshift({ ...item, addedAt: Date.now() });
    this.write(items);
    toast('Saved to your wishlist');
    return true;
  },
  remove(handle) {
    this.write(this.read().filter((i) => i.handle !== handle));
  },
  syncButtons() {
    const handles = new Set(this.read().map((i) => i.handle));
    document.querySelectorAll('[data-dv-wishlist-toggle]').forEach((btn) => {
      const host = btn.closest('[data-dv-product]');
      const active = Boolean(host && handles.has(host.dataset.handle));
      if (btn.classList.contains('is-active') !== active) btn.classList.toggle('is-active', active);
      if (btn.getAttribute('aria-pressed') !== String(active)) btn.setAttribute('aria-pressed', String(active));
    });
  },
  render() {
    const items = this.read();
    this.syncButtons();

    document.querySelectorAll('[data-dv-wishlist-count]').forEach((el) => {
      el.textContent = items.length > 99 ? '99+' : String(items.length);
      el.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-dv-wishlist-count-text]').forEach((el) => {
      el.textContent = items.length ? `(${items.length})` : '';
    });

    const list = document.querySelector('[data-dv-wishlist-list]');
    if (!list) return;

    if (!items.length) {
      list.innerHTML = `
        <div class="dv-empty">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.3-9.4C1.5 7.9 3.6 4.5 7 4.5c2 0 3.7 1.1 5 2.9 1.3-1.8 3-2.9 5-2.9 3.4 0 5.5 3.4 4.3 6.6-1.8 4.8-9.3 9.4-9.3 9.4Z"/></svg>
          <p class="dv-empty__title">Your wishlist is waiting</p>
          <p class="dv-empty__text">Tap the heart on any piece you love to keep it here.</p>
        </div>`;
      return;
    }

    list.innerHTML = items
      .map(
        (item) => `
        <article class="dv-wl-item" data-dv-product data-handle="${escapeHtml(item.handle)}" data-url="${escapeHtml(item.url)}">
          <a class="dv-wl-item__media" href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="120" height="150">` : ''}
          </a>
          <div class="dv-wl-item__info">
            <a class="dv-wl-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
            <p class="dv-wl-item__price">${escapeHtml(item.price || '')}</p>
            <div class="dv-wl-item__actions">
              <button type="button" class="dv-link-btn" data-dv-quickview>Quick view</button>
              <button type="button" class="dv-link-btn dv-link-btn--muted" data-dv-wishlist-remove>Remove</button>
            </div>
          </div>
        </article>`
      )
      .join('');
  },
};

/* ------------------------------------------------------------------ */
/* Quick View                                                         */
/* ------------------------------------------------------------------ */

const QuickView = {
  product: null,
  variant: null,
  imageIndex: 0,
  cache: new Map(),

  get dialog() {
    return document.getElementById('dv-quickview');
  },
  get content() {
    return this.dialog?.querySelector('[data-dv-qv-content]');
  },

  async open(handle, url) {
    const dialog = this.dialog;
    if (!dialog || !handle) {
      if (url) window.location.href = url;
      return;
    }

    this.content.innerHTML = `<div class="dv-qv__loading"><span class="dv-spinner" aria-hidden="true"></span><span class="visually-hidden">Loading</span></div>`;
    openDialog(dialog);

    try {
      let product = this.cache.get(handle);
      if (!product) {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        product = await response.json();
        this.cache.set(handle, product);
      }
      this.product = product;
      this.variant = product.variants.find((v) => v.available) || product.variants[0];
      this.imageIndex = 0;
      this.render();
    } catch (error) {
      console.warn('[dorvahi] quick view failed', error);
      if (url) window.location.href = url;
    }
  },

  images() {
    const p = this.product;
    const list = (p.media || []).filter((m) => m.media_type === 'image').map((m) => m.src);
    return list.length ? list : p.images || [];
  },

  description() {
    const tmp = document.createElement('div');
    tmp.innerHTML = this.product.description || '';
    const text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > 260 ? `${text.slice(0, 257).trim()}…` : text;
  },

  render() {
    const p = this.product;
    const images = this.images();
    const productUrl = `${root}products/${p.handle}`;
    const saved = Wishlist.has(p.handle);
    const hasOptions = !(p.variants.length === 1 && p.options.length === 1 && /default title/i.test(p.variants[0].title));
    const firstImage = images[0] ? sizedImage(images[0], 400) : '';

    const optionsHtml = hasOptions
      ? p.options
          .map((option, index) => {
            const name = typeof option === 'string' ? option : option.name;
            const values = typeof option === 'string' ? [...new Set(p.variants.map((v) => v.options[index]))] : option.values;
            return `
            <fieldset class="dv-qv__option" data-option-index="${index}">
              <legend>${escapeHtml(name)}: <span data-dv-option-value="${index}">${escapeHtml(this.variant.options[index])}</span></legend>
              <div class="dv-qv__values">
                ${values
                  .map(
                    (value) => `<button type="button" class="dv-chip" data-dv-option="${index}" data-value="${escapeHtml(value)}">${escapeHtml(value)}</button>`
                  )
                  .join('')}
              </div>
            </fieldset>`;
          })
          .join('')
      : '';

    this.content.innerHTML = `
      <div class="dv-qv__grid" data-dv-product data-handle="${escapeHtml(p.handle)}" data-title="${escapeHtml(p.title)}" data-url="${escapeHtml(productUrl)}" data-image="${escapeHtml(firstImage)}" data-price="${escapeHtml(money(p.price))}">
        <div class="dv-qv__gallery">
          <div class="dv-qv__stage">
            <img class="dv-qv__main" data-dv-qv-main alt="${escapeHtml(p.title)}" src="${escapeHtml(sizedImage(images[0], 1000))}">
            ${
              images.length > 1
                ? `<button type="button" class="dv-qv__nav dv-qv__nav--prev" data-dv-qv-nav="-1" aria-label="Previous image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg></button>
                   <button type="button" class="dv-qv__nav dv-qv__nav--next" data-dv-qv-nav="1" aria-label="Next image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg></button>`
                : ''
            }
          </div>
          ${
            images.length > 1
              ? `<div class="dv-qv__thumbs">${images
                  .slice(0, 8)
                  .map(
                    (src, i) =>
                      `<button type="button" class="dv-qv__thumb" data-dv-qv-thumb="${i}" aria-label="Show image ${i + 1}"><img src="${escapeHtml(sizedImage(src, 160))}" alt="" loading="lazy"></button>`
                  )
                  .join('')}</div>`
              : ''
          }
        </div>
        <div class="dv-qv__info">
          <p class="dv-eyebrow">${escapeHtml(p.type || p.vendor || 'Dorvahi')}</p>
          <h2 class="dv-qv__title">${escapeHtml(p.title)}</h2>
          <div class="dv-qv__price" data-dv-qv-price></div>
          <p class="dv-qv__tax">Inclusive of all taxes</p>
          <span class="dv-rule" aria-hidden="true"></span>
          ${this.description() ? `<p class="dv-qv__desc">${escapeHtml(this.description())}</p>` : ''}
          ${optionsHtml}
          <div class="dv-qv__buy">
            <div class="dv-qty" role="group" aria-label="Quantity">
              <button type="button" data-dv-qty="-1" aria-label="Decrease quantity">−</button>
              <input type="number" min="1" value="1" data-dv-qty-input aria-label="Quantity">
              <button type="button" data-dv-qty="1" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="dv-btn dv-btn--primary dv-qv__add" data-dv-add>Add to bag</button>
            <button type="button" class="dv-icon-btn dv-wish-btn dv-qv__wish${saved ? ' is-active' : ''}" data-dv-wishlist-toggle aria-pressed="${saved}" aria-label="Save to wishlist">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.3-9.4C1.5 7.9 3.6 4.5 7 4.5c2 0 3.7 1.1 5 2.9 1.3-1.8 3-2.9 5-2.9 3.4 0 5.5 3.4 4.3 6.6-1.8 4.8-9.3 9.4-9.3 9.4Z"/></svg>
            </button>
          </div>
          <p class="dv-qv__error" data-dv-qv-error hidden></p>
          <ul class="dv-qv__perks">
            <li>Free shipping on prepaid orders</li>
            <li>Extra 5% off on prepaid</li>
            <li>COD available up to ₹5,000</li>
          </ul>
          <a class="dv-qv__full" href="${escapeHtml(productUrl)}">View full details <span aria-hidden="true">→</span></a>
        </div>
      </div>`;

    this.updateVariant();
    this.showImage(0);
  },

  showImage(index) {
    const images = this.images();
    if (!images.length) return;
    this.imageIndex = (index + images.length) % images.length;
    const main = this.content.querySelector('[data-dv-qv-main]');
    if (main) {
      main.classList.add('is-swapping');
      const src = sizedImage(images[this.imageIndex], 1000);
      const img = new Image();
      img.onload = img.onerror = () => {
        main.src = src;
        main.classList.remove('is-swapping');
      };
      img.src = src;
    }
    this.content.querySelectorAll('[data-dv-qv-thumb]').forEach((thumb) => {
      thumb.classList.toggle('is-active', Number(thumb.dataset.dvQvThumb) === this.imageIndex);
    });
  },

  selectOption(index, value) {
    const selected = [...this.variant.options];
    selected[index] = value;
    const match =
      this.product.variants.find((v) => v.options.every((opt, i) => opt === selected[i])) ||
      this.product.variants.find((v) => v.options[index] === value && v.available) ||
      this.product.variants.find((v) => v.options[index] === value);
    if (!match) return;
    this.variant = match;
    this.updateVariant();

    const featured = match.featured_image?.src;
    if (featured) {
      const images = this.images();
      const clean = (s) => s.split('?')[0].replace(/^https?:/, '');
      const i = images.findIndex((src) => clean(src) === clean(featured));
      if (i > -1) this.showImage(i);
    }
  },

  updateVariant() {
    const v = this.variant;
    const priceEl = this.content.querySelector('[data-dv-qv-price]');
    if (priceEl) {
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      const off = onSale ? Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) : 0;
      priceEl.innerHTML = `
        <span class="dv-qv__price-now">${escapeHtml(money(v.price))}</span>
        ${onSale ? `<s class="dv-qv__price-was">${escapeHtml(money(v.compare_at_price))}</s><span class="dv-qv__save">${off}% off</span>` : ''}`;
    }

    this.content.querySelectorAll('[data-dv-option]').forEach((chip) => {
      const index = Number(chip.dataset.dvOption);
      const value = chip.dataset.value;
      chip.classList.toggle('is-selected', v.options[index] === value);
      chip.setAttribute('aria-pressed', String(v.options[index] === value));
      const candidate = [...v.options];
      candidate[index] = value;
      const exists = this.product.variants.find((x) => x.options.every((o, i) => o === candidate[i]));
      chip.classList.toggle('is-unavailable', !exists || !exists.available);
    });
    this.content.querySelectorAll('[data-dv-option-value]').forEach((el) => {
      el.textContent = v.options[Number(el.dataset.dvOptionValue)];
    });

    const add = this.content.querySelector('[data-dv-add]');
    if (add) {
      add.disabled = !v.available;
      add.textContent = v.available ? 'Add to bag' : 'Sold out';
    }
    const error = this.content.querySelector('[data-dv-qv-error]');
    if (error) error.hidden = true;
  },

  async addToCart(button) {
    const v = this.variant;
    if (!v || !v.available) return;
    const qtyInput = this.content.querySelector('[data-dv-qty-input]');
    const quantity = Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);
    const errorEl = this.content.querySelector('[data-dv-qv-error]');

    button.disabled = true;
    button.classList.add('is-loading');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {}

    const { CartLinesUpdateEvent } = events || {};
    const deferred = CartLinesUpdateEvent?.createPromise ? CartLinesUpdateEvent.createPromise() : null;

    // Dispatch from inside the modal so Horizon's cart drawer waits for this dialog to close before opening.
    if (CartLinesUpdateEvent && deferred) {
      button.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(v.id), quantity }],
          promise: deferred.promise,
        })
      );
    }

    const body = new FormData();
    body.append('id', String(v.id));
    body.append('quantity', String(quantity));
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    try {
      const addUrl = window.Theme?.routes?.cart_add_url || `${root}cart/add.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const result = await response.json();

      if (result.status) {
        const message = result.description || result.message || 'Could not add to bag';
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        const cart = await fetch(`${root}cart.js`).then((r) => r.json());
        deferred?.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'dorvahi-quick-view', didError: true },
        });
        return;
      }

      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'dorvahi-quick-view',
          sourceId: 'dv-quickview',
          itemCount: quantity,
          productId: String(this.product.id),
          sections: result.sections,
          didError: false,
        },
      });

      button.classList.add('is-added');
      button.textContent = 'Added ✓';
      toast('Added to your bag');
      setTimeout(() => {
        const dialog = this.dialog;
        // Horizon only auto-opens the cart drawer when that theme setting is on; open it ourselves otherwise.
        if (!document.querySelector('cart-drawer-component[auto-open]')) {
          dialog?.addEventListener('close', () => document.getElementById('cart-drawer')?.open?.(), { once: true });
        }
        closeDialog(dialog);
      }, 650);
    } catch (error) {
      console.warn('[dorvahi] add to cart failed', error);
      deferred?.reject(error);
      if (errorEl) {
        errorEl.textContent = 'Something went wrong. Please try again.';
        errorEl.hidden = false;
      }
    } finally {
      button.classList.remove('is-loading');
      setTimeout(() => {
        if (!button.isConnected) return;
        button.disabled = !this.variant?.available;
        button.classList.remove('is-added');
        button.textContent = this.variant?.available ? 'Add to bag' : 'Sold out';
      }, 1600);
    }
  },
};

/* ------------------------------------------------------------------ */
/* Event wiring                                                       */
/* ------------------------------------------------------------------ */

const productData = (el) => {
  const host = el.closest('[data-dv-product]');
  if (!host) return null;
  return {
    handle: host.dataset.handle,
    title: host.dataset.title || '',
    url: host.dataset.url || '',
    image: host.dataset.image || '',
    price: host.dataset.price || '',
  };
};

// Capture phase so product-card link / view-transition handlers never see these clicks.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const wish = target.closest('[data-dv-wishlist-toggle]');
    if (wish) {
      event.preventDefault();
      event.stopPropagation();
      const data = productData(wish);
      if (!data?.handle) return;
      const active = Wishlist.toggle(data);
      wish.classList.remove('dv-pop');
      void wish.offsetWidth;
      if (active) wish.classList.add('dv-pop');
      return;
    }

    const qv = target.closest('[data-dv-quickview]');
    if (qv) {
      event.preventDefault();
      event.stopPropagation();
      const data = productData(qv);
      const drawer = document.getElementById('dv-wishlist');
      if (drawer?.open) closeDialog(drawer);
      QuickView.open(data?.handle, data?.url);
      return;
    }

    const remove = target.closest('[data-dv-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      const data = productData(remove);
      if (data?.handle) Wishlist.remove(data.handle);
      return;
    }

    const openWishlist = target.closest('[data-dv-wishlist-open]');
    if (openWishlist) {
      event.preventDefault();
      event.stopPropagation();
      Wishlist.render();
      openDialog(document.getElementById('dv-wishlist'));
      return;
    }

    const close = target.closest('[data-dv-close]');
    if (close) {
      closeDialog(close.closest('dialog'));
      return;
    }

    // Click on the dialog backdrop
    if (target.matches('dialog.dv-drawer, dialog.dv-qv, dialog.dv-sc')) {
      closeDialog(target);
      return;
    }

    if (!target.closest('#dv-quickview')) return;

    const option = target.closest('[data-dv-option]');
    if (option) {
      QuickView.selectOption(Number(option.dataset.dvOption), option.dataset.value);
      return;
    }
    const thumb = target.closest('[data-dv-qv-thumb]');
    if (thumb) {
      QuickView.showImage(Number(thumb.dataset.dvQvThumb));
      return;
    }
    const nav = target.closest('[data-dv-qv-nav]');
    if (nav) {
      QuickView.showImage(QuickView.imageIndex + Number(nav.dataset.dvQvNav));
      return;
    }
    const qty = target.closest('[data-dv-qty]');
    if (qty) {
      const input = QuickView.content.querySelector('[data-dv-qty-input]');
      if (input) input.value = String(Math.max(1, (parseInt(input.value, 10) || 1) + Number(qty.dataset.dvQty)));
      return;
    }
    const add = target.closest('[data-dv-add]');
    if (add) QuickView.addToCart(add);
  },
  true
);

// ESC: animate the close instead of the native instant close.
document.addEventListener('cancel', (event) => {
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement && dialog.matches('.dv-drawer, .dv-qv, .dv-sc')) {
    event.preventDefault();
    closeDialog(dialog);
  }
}, true);

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) Wishlist.render();
});

// Product cards are re-rendered by filters, pagination and recommendations — keep hearts in sync.
let syncQueued = false;
const isRelevantNode = (node) =>
  node.nodeType === 1 &&
  !node.closest('.dv-drawer, .dv-qv, [data-dv-toast]') &&
  (node.matches('[data-dv-product], product-card, .shopify-section') ||
    Boolean(node.querySelector('[data-dv-product], product-card')));

new MutationObserver((records) => {
  if (syncQueued) return;
  if (!records.some((r) => [...r.addedNodes].some(isRelevantNode))) return;
  syncQueued = true;
  requestAnimationFrame(() => {
    syncQueued = false;
    Wishlist.syncButtons();
    observeReveal();
  });
}).observe(document.body, { childList: true, subtree: true });

/* ------------------------------------------------------------------ */
/* Scroll reveal + header state                                       */
/* ------------------------------------------------------------------ */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = document.documentElement.classList.contains('shopify-design-mode');

const revealObserver =
  !reduceMotion && !designMode && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('dv-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0 }
      )
    : null;

function observeReveal() {
  if (!revealObserver) return;
  const targets = document.querySelectorAll(
    '#MainContent > .shopify-section:not(:first-child):not([data-dv-reveal]), #MainContent product-card:not([data-dv-reveal]), #MainContent .collection-card:not([data-dv-reveal])'
  );
  targets.forEach((el, i) => {
    el.setAttribute('data-dv-reveal', '');
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.92) return; // already on screen: leave it visible
    if (el.matches('product-card, .collection-card')) {
      const item = el.closest('li') || el;
      const index = item.parentElement ? [...item.parentElement.children].indexOf(item) : 0;
      el.style.setProperty('--dv-delay', `${(Math.max(index, 0) % 4) * 80}ms`);
    }
    el.classList.add('dv-reveal');
    revealObserver.observe(el);
  });
}

let ticking = false;
const onScroll = () => {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    document.body.classList.toggle('dv-scrolled', window.scrollY > 24);
    ticking = false;
  });
};
window.addEventListener('scroll', onScroll, { passive: true });

/* ------------------------------------------------------------------ */
/* Product page                                                       */
/* ------------------------------------------------------------------ */

const DETAILS_HEADING = /^product details\s*:?$/i;
const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function splitDescription(pdp) {
  const desc = pdp.querySelector('[data-dv-desc]');
  const detailsItem = pdp.querySelector('[data-dv-details]');
  const detailsBody = pdp.querySelector('[data-dv-details-body]');
  if (!desc || !detailsItem || !detailsBody || desc.dataset.dvSplit) return;
  desc.dataset.dvSplit = 'true';

  const heading = [...desc.children].find((el) => DETAILS_HEADING.test(el.textContent.trim()));
  if (!heading) return;

  const moved = [];
  let node = heading.nextSibling;
  while (node) {
    const next = node.nextSibling;
    moved.push(node);
    node = next;
  }
  heading.remove();
  moved.forEach((n) => detailsBody.appendChild(n));

  // "Fabric: Cotton" style bullet lists become a two-column spec table.
  const items = [...detailsBody.querySelectorAll('li')];
  const pairs = items.map((li) => li.textContent.trim().match(/^([^:]{2,40}):\s*(.+)$/));
  if (items.length && pairs.every(Boolean)) {
    const dl = document.createElement('dl');
    dl.className = 'dv-spec';
    pairs.forEach(([, label, value]) => {
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = label.trim();
      dd.textContent = value.trim();
      dl.append(dt, dd);
    });
    const firstList = items[0].closest('ul, ol');
    firstList?.before(dl);
    detailsBody.querySelectorAll('ul, ol').forEach((list) => list.remove());
  }

  [...detailsBody.querySelectorAll('p')].forEach((p) => {
    if (!p.textContent.trim() && !p.querySelector('img')) p.remove();
  });
  [...desc.querySelectorAll('p')].forEach((p) => {
    if (!p.textContent.trim() && !p.querySelector('img')) p.remove();
  });

  if (detailsBody.textContent.trim()) detailsItem.hidden = false;
}

function placeClone(source, anchor, where) {
  if (!source || !anchor) return;
  const marker = source.hasAttribute('data-dv-pdp-eyebrow') ? 'eyebrow' : 'tax';
  if (anchor.parentElement?.querySelector(`[data-dv-clone="${marker}"]`)) return;
  const clone = source.cloneNode(true);
  clone.hidden = false;
  clone.removeAttribute('data-dv-pdp-eyebrow');
  clone.removeAttribute('data-dv-pdp-tax');
  clone.setAttribute('data-dv-clone', marker);
  anchor[where](clone);
}

// Put the size chart link next to the Size option (falls back to its own spot in the extras block).
function placeSizeGuide(pdp, details) {
  const original = pdp.querySelector('[data-dv-size-guide]:not([data-dv-clone])');
  if (!original) return;
  const fieldsets = [...details.querySelectorAll('variant-picker fieldset')];
  const fieldset =
    fieldsets.find((fs) => /size/i.test(fs.querySelector('legend')?.textContent || '')) || fieldsets[0];
  if (!fieldset) {
    original.hidden = false;
    return;
  }
  if (!fieldset.querySelector('[data-dv-clone="size-guide"]')) {
    const clone = original.cloneNode(true);
    clone.hidden = false;
    clone.setAttribute('data-dv-clone', 'size-guide');
    clone.classList.add('dv-size-guide--inline');
    const legend = fieldset.querySelector('legend');
    if (legend) {
      legend.appendChild(clone);
      legend.classList.add('dv-legend-with-guide');
    } else {
      fieldset.prepend(clone);
    }
  }
  original.hidden = true;
}

function openSizeChart(trigger) {
  const scope = trigger.closest('.product-information, product-component, .quick-add-modal, [data-dv-pdp]') || document;
  const tpl = scope.querySelector('[data-dv-size-chart-tpl]') || document.querySelector('[data-dv-size-chart-tpl]');
  if (!tpl) return;
  let dialog = document.getElementById('dv-size-chart');
  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.id = 'dv-size-chart';
    dialog.className = 'dv-sc';
    dialog.setAttribute('aria-label', 'Size chart');
    document.body.appendChild(dialog);
  }
  dialog.innerHTML = '';
  dialog.appendChild(tpl.content.cloneNode(true));
  openDialog(dialog);
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const trigger = target.closest('[data-dv-size-chart-open]');
  if (trigger) {
    event.preventDefault();
    openSizeChart(trigger);
    return;
  }
  const img = target.closest('#dv-size-chart .dv-sc__img');
  if (img) {
    const body = img.closest('[data-dv-sc-body]');
    const zoomed = body.classList.toggle('is-zoomed');
    if (zoomed) {
      const rect = img.getBoundingClientRect();
      const fx = (event.clientX - rect.left) / rect.width;
      const fy = (event.clientY - rect.top) / rect.height;
      requestAnimationFrame(() => {
        body.scrollLeft = fx * body.scrollWidth - body.clientWidth / 2;
        body.scrollTop = fy * body.scrollHeight - body.clientHeight / 2;
      });
    }
  }
});

function enhancePdp() {
  const pdp = document.querySelector('[data-dv-pdp]');
  if (!pdp) return;
  const details = pdp.closest('.product-details') || document;

  splitDescription(pdp);

  const title = details.querySelector('h1');
  const titleBlock = title?.closest('.text-block') || title;
  placeClone(pdp.querySelector('[data-dv-pdp-eyebrow]'), titleBlock, 'before');
  placeClone(pdp.querySelector('[data-dv-pdp-tax]'), details.querySelector('product-price'), 'after');
  placeSizeGuide(pdp, details);
}

// Smooth accordion open / close
document.addEventListener('click', (event) => {
  const summary = event.target instanceof Element ? event.target.closest('[data-dv-acc] > summary') : null;
  if (!summary || reduceMotionQuery.matches) return;
  const item = summary.parentElement;
  const body = item.querySelector('.dv-acc__body');
  if (!body || item.dataset.animating) return;
  event.preventDefault();
  item.dataset.animating = 'true';

  const done = () => {
    delete item.dataset.animating;
    body.style.height = '';
  };

  if (item.open) {
    const start = body.offsetHeight;
    body.animate([{ height: `${start}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
      duration: 380,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    }).onfinish = () => {
      item.open = false;
      done();
    };
  } else {
    item.open = true;
    const end = body.offsetHeight;
    body.animate([{ height: '0px', opacity: 0 }, { height: `${end}px`, opacity: 1 }], {
      duration: 460,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    }).onfinish = done;
  }
});

// Share
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-dv-share]') : null;
  if (!btn) return;
  const url = btn.dataset.shareUrl || window.location.href;
  const title = btn.dataset.shareTitle || document.title;
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
    } catch (e) {}
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    toast('Link copied');
  } catch (e) {
    window.prompt('Copy this link', url);
  }
});

// Cursor-following zoom on the main product gallery
const zoomQuery = window.matchMedia('(hover: hover) and (min-width: 750px)');
document.addEventListener('mousemove', (event) => {
  if (!zoomQuery.matches) return;
  const media = event.target instanceof Element ? event.target.closest('.product-information__media .product-media') : null;
  document.querySelectorAll('.product-media.dv-zooming').forEach((el) => {
    if (el !== media) el.classList.remove('dv-zooming');
  });
  if (!media) return;
  const rect = media.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  media.querySelector('.product-media__image')?.style.setProperty('transform-origin', `${x}% ${y}%`);
  media.classList.add('dv-zooming');
});
document.addEventListener('mouseleave', () => {
  document.querySelectorAll('.product-media.dv-zooming').forEach((el) => el.classList.remove('dv-zooming'));
});

// Horizon morphs product details on variant change; re-apply enhancements afterwards.
let pdpQueued = false;
if (document.querySelector('[data-dv-pdp]')) {
  new MutationObserver(() => {
    if (pdpQueued) return;
    pdpQueued = true;
    requestAnimationFrame(() => {
      pdpQueued = false;
      enhancePdp();
    });
  }).observe(document.querySelector('.product-information') || document.body, { childList: true, subtree: true });
}

/* ------------------------------------------------------------------ */
/* Collection carousel (continuous rotation)                          */
/* ------------------------------------------------------------------ */

class DorvahiCarousel {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-dv-cc-viewport]');
    this.track = root.querySelector('[data-dv-cc-track]');
    this.originals = [...this.track.children];
    this.speed = reduceMotionQuery.matches ? 0 : Number(root.dataset.speed || 0); // px per second
    this.offset = 0;
    this.setWidth = 0;
    this.paused = false;
    this.visible = true;
    this.anim = null; // eased arrow movement
    this.drag = null;
    this.last = 0;

    this.build();
    this.bind();
    root.classList.add('is-ready');
    this.frame = requestAnimationFrame(this.tick);
  }

  build() {
    this.track.querySelectorAll('[data-dv-clone]').forEach((el) => el.remove());
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const first = this.originals[0];
    const lastEl = this.originals[this.originals.length - 1];
    if (!first) return;
    this.setWidth = lastEl.offsetLeft + lastEl.offsetWidth - first.offsetLeft + gap;
    if (this.setWidth <= 0) return;

    // Enough copies that the track always covers the viewport twice over.
    const copies = Math.max(1, Math.ceil((this.viewport.clientWidth * 2) / this.setWidth));
    for (let i = 0; i < copies; i++) {
      this.originals.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('data-dv-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        this.track.appendChild(clone);
      });
    }
    this.stepSize = first.offsetWidth + gap;
    this.render();
  }

  bind() {
    const pause = () => (this.paused = true);
    const resume = () => (this.paused = false);
    this.root.addEventListener('mouseenter', pause);
    this.root.addEventListener('mouseleave', resume);
    this.root.addEventListener('focusin', pause);
    this.root.addEventListener('focusout', resume);

    this.root.querySelector('[data-dv-cc-prev]')?.addEventListener('click', () => this.step(-1));
    this.root.querySelector('[data-dv-cc-next]')?.addEventListener('click', () => this.step(1));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting)).observe(this.root);
    }

    // Drag / swipe
    this.viewport.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      this.drag = { x: e.clientX, y: e.clientY, start: this.offset, moved: false, id: e.pointerId, axis: null };
      this.anim = null;
    });
    window.addEventListener('pointermove', (e) => {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      if (!d.axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        d.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (d.axis === 'x') {
          d.moved = true;
          this.viewport.classList.add('is-dragging');
          try {
            this.viewport.setPointerCapture(e.pointerId);
          } catch (err) {}
        }
      }
      if (d.axis === 'x') {
        this.offset = d.start - dx;
        this.render();
      }
    });
    const end = (e) => {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      this.viewport.classList.remove('is-dragging');
      this.drag = null;
      if (d.moved) {
        this.justDragged = true;
        setTimeout(() => (this.justDragged = false), 80);
      }
    };
    // Swallow the click that immediately follows a drag so cards don't open accidentally.
    this.viewport.addEventListener(
      'click',
      (ev) => {
        if (!this.justDragged) return;
        ev.preventDefault();
        ev.stopPropagation();
      },
      true
    );
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    this.viewport.addEventListener('dragstart', (e) => e.preventDefault());

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.build(), 200);
    });
  }

  step(direction) {
    const from = this.offset;
    // Snap to the next card boundary in the chosen direction.
    const base = Math.round(from / this.stepSize) * this.stepSize;
    const to = base + direction * this.stepSize;
    this.anim = { from, to, start: performance.now(), duration: 700 };
  }

  render() {
    if (!this.setWidth) return;
    this.offset = ((this.offset % this.setWidth) + this.setWidth) % this.setWidth;
    this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
  }

  tick = (now) => {
    const dt = this.last ? Math.min(now - this.last, 64) / 1000 : 0;
    this.last = now;

    if (this.anim) {
      const { from, to, start, duration } = this.anim;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      this.offset = from + (to - from) * eased;
      this.render();
      if (t === 1) this.anim = null;
    } else if (!this.drag && !this.paused && this.visible && !document.hidden && this.speed > 0) {
      this.offset += this.speed * dt;
      this.render();
    }
    this.frame = requestAnimationFrame(this.tick);
  };

  destroy() {
    cancelAnimationFrame(this.frame);
  }
}

function initCarousels(scope = document) {
  scope.querySelectorAll('[data-dv-cc]').forEach((root) => {
    if (root.dvCarousel || !root.querySelector('[data-dv-cc-track] > *')) return;
    root.dvCarousel = new DorvahiCarousel(root);
  });
}

// Theme editor: sections and blocks re-render in place.
document.addEventListener('shopify:section:load', (e) => initCarousels(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-dv-cc]').forEach((root) => root.dvCarousel?.destroy());
});
document.addEventListener('shopify:block:select', (e) => {
  const root = e.target.closest?.('[data-dv-cc]');
  if (root?.dvCarousel) root.dvCarousel.paused = true;
});
document.addEventListener('shopify:block:deselect', (e) => {
  const root = e.target.closest?.('[data-dv-cc]');
  if (root?.dvCarousel) root.dvCarousel.paused = false;
});

/* ------------------------------------------------------------------ */
/* WhatsApp greeting                                                  */
/* ------------------------------------------------------------------ */

const WA_KEY = 'dorvahi:wa-greeted';
function initWhatsApp() {
  const bubble = document.querySelector('[data-dv-wa-bubble]');
  if (!bubble) return;
  let seen = false;
  try {
    seen = sessionStorage.getItem(WA_KEY) === '1';
  } catch (e) {}
  if (seen) return;

  const dismiss = () => {
    bubble.hidden = true;
    try {
      sessionStorage.setItem(WA_KEY, '1');
    } catch (e) {}
  };
  bubble.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (t?.closest('[data-dv-wa-dismiss], .dv-wa__bubble-cta')) dismiss();
  });
  setTimeout(() => {
    if (!document.documentElement.classList.contains('dv-scroll-lock')) bubble.hidden = false;
    setTimeout(dismiss, 12000);
  }, 6000);
}

/* ------------------------------------------------------------------ */
/* Boot                                                               */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Circular collections                                               */
/* ------------------------------------------------------------------ */


// Staggered entrance once the row scrolls into view
function armCirclesEntrance(root) {
  if (reduceMotionQuery.matches || designMode || !('IntersectionObserver' in window)) return;
  if (root.getBoundingClientRect().top <= window.innerHeight * 0.9) return;
  root.classList.add('is-armed');
  const io = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;
      root.classList.add('is-in');
      io.disconnect();
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  io.observe(root);
}

function initCircles(scope = document) {
  scope.querySelectorAll('[data-dv-circles]').forEach((root) => {
    if (root.dvCircles) return;
    const list = root.querySelector('[data-dv-circles-list]');
    if (!list) return;
    root.dvCircles = true;

    // Rotating mode is driven by DorvahiCarousel (data-dv-cc); only the entrance animation applies here.
    if (root.hasAttribute('data-dv-cc')) {
      armCirclesEntrance(root);
      return;
    }

    const prev = root.querySelector('[data-dv-circles-prev]');
    const next = root.querySelector('[data-dv-circles-next]');

    const update = () => {
      const overflowing = list.scrollWidth > list.clientWidth + 2;
      root.classList.toggle('is-overflowing', overflowing);
      if (prev) prev.disabled = list.scrollLeft <= 2;
      if (next) next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
    };

    const page = (dir) => list.scrollBy({ left: dir * list.clientWidth * 0.7, behavior: 'smooth' });
    prev?.addEventListener('click', () => page(-1));
    next?.addEventListener('click', () => page(1));
    list.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
    update();

    // Mouse drag-to-scroll (touch keeps native swiping)
    let drag = null;
    list.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: list.scrollLeft, moved: false };
    });
    window.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 5) {
        drag.moved = true;
        list.classList.add('is-dragging');
      }
      if (drag.moved) list.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!drag) return;
      const moved = drag.moved;
      drag = null;
      list.classList.remove('is-dragging');
      if (moved) {
        root.dvJustDragged = true;
        setTimeout(() => (root.dvJustDragged = false), 80);
      }
    });
    list.addEventListener(
      'click',
      (e) => {
        if (!root.dvJustDragged) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
    list.addEventListener('dragstart', (e) => e.preventDefault());

    armCirclesEntrance(root);
  });
}

document.addEventListener('shopify:section:load', (e) => initCircles(e.target));

enhancePdp();
initWhatsApp();
initCarousels();
initCircles();

Wishlist.render();
observeReveal();
onScroll();
